/*
  Section controller: product-shorts-slider
  Handles per-section state, IntersectionObserver-driven autoplay,
  scroll snap navigation, mute/unmute, keyboard nav, theme-editor lifecycle.
*/

(function () {
  var SECTION_SELECTOR = '.product-shorts-slider';
  var instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function ProductShortsSlider(section) {
    this.section = section;
    this.displayMode = section.dataset.displayMode || 'slider';
    this.cleanupFns = [];

    if (this.displayMode === 'grid') {
      this.initGrid();
      return;
    }

    this.track = section.querySelector('.product-shorts-slider__track');
    this.slides = Array.prototype.slice.call(section.querySelectorAll('.product-shorts-slide'));
    this.dots = Array.prototype.slice.call(section.querySelectorAll('.product-shorts-slider__dot'));
    this.prevButton = section.querySelector('[data-shorts-prev]');
    this.nextButton = section.querySelector('[data-shorts-next]');

    this.currentIndex = 0;
    this.muted = true;
    this.autoplayDuration = (parseInt(section.dataset.autoplayDuration, 10) || 4) * 1000;
    this.autoplayEnabled = section.dataset.autoplayEnabled === 'true' && !prefersReducedMotion();
    this.imageAdvanceTimeout = null;
    this.sectionInView = false;

    if (!this.track || !this.slides.length) return;

    this.slides.forEach(function (slide, i) {
      slide.dataset.slideIndex = String(i);
    });

    this.bindEvents();
    this.observeSection();
    this.observeSlides();
    this.syncMuteUI();
    this.updateActiveIndex(0, { scroll: false });
  }

  ProductShortsSlider.prototype.initGrid = function () {
    var self = this;
    this.autoplayEnabled = this.section.dataset.autoplayEnabled === 'true' && !prefersReducedMotion();
    this.modal = this.section.querySelector('[data-shorts-modal]');
    this.modalVideo = this.modal && this.modal.querySelector('.shorts-modal__video');
    this.modalMuteButton = this.modal && this.modal.querySelector('.shorts-modal__mute');
    this.modalMuted = false;
    this.gridSectionInView = false;
    this.gridTrack = this.section.querySelector('.product-shorts-grid__track');
    this.gridItems = Array.prototype.slice.call(this.section.querySelectorAll('.product-shorts-grid__item--video'));

    this.gridItems.forEach(function (item) {
      var video = item.querySelector('video');
      var thumb = item.querySelector('.product-shorts-grid__thumb');

      if (video && thumb) {
        var syncPlayingState = function () {
          thumb.dataset.playing = video.paused ? 'false' : 'true';
        };
        video.addEventListener('play', syncPlayingState);
        video.addEventListener('pause', syncPlayingState);
        self.cleanupFns.push(function () {
          video.removeEventListener('play', syncPlayingState);
          video.removeEventListener('pause', syncPlayingState);
        });
      }

      var handler = function (e) {
        e.preventDefault();
        var src = item.dataset.videoSrc;
        if (src) self.openModal(src);
      };
      item.addEventListener('click', handler);
      self.cleanupFns.push(function () { item.removeEventListener('click', handler); });
    });

    this.observeGridSection();

    if (!this.modal) return;

    // Backdrop click-to-close: only when target is the modal itself, not the content.
    var modalClickHandler = function (e) {
      if (e.target === self.modal) self.closeModal();
    };
    this.modal.addEventListener('click', modalClickHandler);
    this.cleanupFns.push(function () { self.modal.removeEventListener('click', modalClickHandler); });

    var closeTriggers = this.modal.querySelectorAll('[data-modal-close]');
    closeTriggers.forEach(function (el) {
      var h = function (e) { e.stopPropagation(); self.closeModal(); };
      el.addEventListener('click', h);
      self.cleanupFns.push(function () { el.removeEventListener('click', h); });
    });

    if (this.modalMuteButton) {
      var muteH = function (e) { e.stopPropagation(); self.toggleModalMute(); };
      this.modalMuteButton.addEventListener('click', muteH);
      self.cleanupFns.push(function () { self.modalMuteButton.removeEventListener('click', muteH); });
    }

    var keyHandler = function (e) {
      if (e.key === 'Escape' && self.modal.getAttribute('aria-hidden') === 'false') {
        self.closeModal();
      }
    };
    document.addEventListener('keydown', keyHandler);
    self.cleanupFns.push(function () { document.removeEventListener('keydown', keyHandler); });

    if (this.modalVideo) {
      var endedHandler = function () { self.closeModal(); };
      this.modalVideo.addEventListener('ended', endedHandler);
      self.cleanupFns.push(function () { self.modalVideo.removeEventListener('ended', endedHandler); });
    }
  };

  ProductShortsSlider.prototype.observeGridSection = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.gridSectionInView = true;
      this.playAllGridVideos();
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.gridSectionInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          self.playAllGridVideos();
        } else {
          self.pauseGridVideos();
        }
      });
    }, { threshold: 0.15 });
    observer.observe(this.section);
    this.cleanupFns.push(function () { observer.disconnect(); });
  };

  ProductShortsSlider.prototype.playAllGridVideos = function () {
    if (!this.gridItems.length || !this.gridSectionInView || !this.autoplayEnabled) return;
    if (this.modal && this.modal.getAttribute('aria-hidden') === 'false') return;

    this.gridItems.forEach(function (item) {
      var video = item.querySelector('video');
      var thumb = item.querySelector('.product-shorts-grid__thumb');
      if (!video) return;

      video.muted = true;
      video.loop = true;

      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () { /* autoplay blocked */ });
      }

      if (thumb) thumb.dataset.playing = video.paused ? 'false' : 'true';
    });
  };

  ProductShortsSlider.prototype.pauseGridVideos = function () {
    this.gridItems.forEach(function (item) {
      var video = item.querySelector('video');
      var thumb = item.querySelector('.product-shorts-grid__thumb');
      if (video && !video.paused) video.pause();
      if (thumb) thumb.dataset.playing = 'false';
    });
  };

  ProductShortsSlider.prototype.openModal = function (src) {
    if (!this.modal || !this.modalVideo || !src) return;
    this.pauseGridVideos();
    this.modalVideo.src = src;
    this.modalMuted = false;
    this.modalVideo.muted = false;
    this.syncModalMuteUI();
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var p = this.modalVideo.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () { /* autoplay with sound blocked — user can tap mute toggle */ });
    }
  };

  ProductShortsSlider.prototype.closeModal = function () {
    if (!this.modal || !this.modalVideo) return;
    this.modalVideo.pause();
    this.modalVideo.removeAttribute('src');
    this.modalVideo.load();
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.gridSectionInView) this.playAllGridVideos();
  };

  ProductShortsSlider.prototype.toggleModalMute = function () {
    if (!this.modalVideo) return;
    this.modalMuted = !this.modalMuted;
    this.modalVideo.muted = this.modalMuted;
    this.syncModalMuteUI();
  };

  ProductShortsSlider.prototype.syncModalMuteUI = function () {
    if (!this.modalMuteButton) return;
    this.modalMuteButton.dataset.modalMuted = this.modalMuted ? 'true' : 'false';
    this.modalMuteButton.setAttribute('aria-label', this.modalMuted ? 'Unmute video' : 'Mute video');
  };

  ProductShortsSlider.prototype.bindEvents = function () {
    var self = this;

    this.dots.forEach(function (dot, index) {
      var handler = function () { self.goToSlide(index); };
      dot.addEventListener('click', handler);
      self.cleanupFns.push(function () { dot.removeEventListener('click', handler); });
    });

    if (this.prevButton) {
      var prevHandler = function () { self.goToSlide(self.currentIndex - 1); };
      this.prevButton.addEventListener('click', prevHandler);
      this.cleanupFns.push(function () { self.prevButton.removeEventListener('click', prevHandler); });
    }

    if (this.nextButton) {
      var nextHandler = function () { self.goToSlide(self.currentIndex + 1); };
      this.nextButton.addEventListener('click', nextHandler);
      this.cleanupFns.push(function () { self.nextButton.removeEventListener('click', nextHandler); });
    }

    var keyHandler = function (e) {
      if (!self.section.contains(document.activeElement)) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); self.goToSlide(self.currentIndex + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); self.goToSlide(self.currentIndex - 1); }
    };
    this.section.addEventListener('keydown', keyHandler);
    this.cleanupFns.push(function () { self.section.removeEventListener('keydown', keyHandler); });

    var scrollSync = this.debounce(function () { self.syncIndexFromScroll(); }, 80);
    this.track.addEventListener('scroll', scrollSync, { passive: true });
    this.cleanupFns.push(function () { self.track.removeEventListener('scroll', scrollSync); });

    this.slides.forEach(function (slide) {
      var media = slide.querySelector('.product-shorts-slide__media');
      var video = slide.querySelector('video');
      var playButton = slide.querySelector('.product-shorts-slide__media-play');
      var muteButton = slide.querySelector('.product-shorts-slide__media-mute');

      if (media && video) {
        var mediaClick = function (e) {
          if (muteButton && (e.target === muteButton || muteButton.contains(e.target))) return;
          if (playButton && (e.target === playButton || playButton.contains(e.target))) return;
          self.togglePlay(slide);
        };
        media.addEventListener('click', mediaClick);
        self.cleanupFns.push(function () { media.removeEventListener('click', mediaClick); });

        var endedHandler = function () {
          self.setPlayingState(playButton, false);
          if (self.autoplayEnabled && self.sectionInView) {
            self.goToSlide(self.currentIndex + 1);
          }
        };
        video.addEventListener('ended', endedHandler);
        self.cleanupFns.push(function () { video.removeEventListener('ended', endedHandler); });

        var pauseHandler = function () {
          if (!video.ended) self.setPlayingState(playButton, false);
        };
        video.addEventListener('pause', pauseHandler);
        self.cleanupFns.push(function () { video.removeEventListener('pause', pauseHandler); });

        var playHandler = function () { self.setPlayingState(playButton, true); };
        video.addEventListener('play', playHandler);
        self.cleanupFns.push(function () { video.removeEventListener('play', playHandler); });
      }

      if (playButton) {
        var playClick = function (e) { e.stopPropagation(); self.togglePlay(slide); };
        playButton.addEventListener('click', playClick);
        self.cleanupFns.push(function () { playButton.removeEventListener('click', playClick); });
      }

      if (muteButton) {
        var muteClick = function (e) { e.stopPropagation(); self.toggleMute(); };
        muteButton.addEventListener('click', muteClick);
        self.cleanupFns.push(function () { muteButton.removeEventListener('click', muteClick); });
      }
    });
  };

  ProductShortsSlider.prototype.observeSection = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.sectionInView = true;
      this.maybeStartAutoAdvance();
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.sectionInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          self.maybeStartAutoAdvance();
        } else {
          self.stopAutoAdvance();
          self.pauseAllVideos();
        }
      });
    }, { threshold: 0.2 });
    observer.observe(this.section);
    this.cleanupFns.push(function () { observer.disconnect(); });
  };

  ProductShortsSlider.prototype.observeSlides = function () {
    if (!('IntersectionObserver' in window)) return;
    var self = this;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var slide = entry.target;
        var video = slide.querySelector('video');
        if (!video) return;
        if (!entry.isIntersecting) {
          if (!video.paused) {
            video.pause();
            video.currentTime = 0;
          }
        }
      });
    }, { root: this.track, threshold: 0.6 });
    this.slides.forEach(function (slide) { observer.observe(slide); });
    this.cleanupFns.push(function () { observer.disconnect(); });
  };

  ProductShortsSlider.prototype.syncIndexFromScroll = function () {
    var scrollLeft = this.track.scrollLeft;
    var trackRect = this.track.getBoundingClientRect();
    var trackCenter = scrollLeft + trackRect.width / 2;
    var closestIndex = 0;
    var closestDistance = Infinity;
    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      var distance = Math.abs(slideCenter - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }
    if (closestIndex !== this.currentIndex) {
      this.updateActiveIndex(closestIndex, { scroll: false });
    }
  };

  ProductShortsSlider.prototype.goToSlide = function (rawIndex) {
    var count = this.slides.length;
    if (count === 0) return;
    var index = ((rawIndex % count) + count) % count;
    this.updateActiveIndex(index, { scroll: true });
  };

  ProductShortsSlider.prototype.updateActiveIndex = function (index, opts) {
    opts = opts || {};
    var prevIndex = this.currentIndex;
    this.currentIndex = index;

    this.dots.forEach(function (dot, i) {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;

    if (opts.scroll) {
      var slide = this.slides[index];
      var targetLeft = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      this.track.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    if (prevIndex !== index) {
      var prevVideo = this.slides[prevIndex] && this.slides[prevIndex].querySelector('video');
      if (prevVideo && !prevVideo.paused) {
        prevVideo.pause();
        prevVideo.currentTime = 0;
      }
    }

    this.maybeStartAutoAdvance();
  };

  ProductShortsSlider.prototype.maybeStartAutoAdvance = function () {
    this.stopAutoAdvance();
    if (!this.autoplayEnabled || !this.sectionInView) return;

    var slide = this.slides[this.currentIndex];
    if (!slide) return;
    var video = slide.querySelector('video');

    if (video) {
      // Video slides advance on the `ended` event — no timer.
      // Try to autoplay muted; user must click for sound.
      var self = this;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () { /* autoplay blocked — leave paused */ });
      }
      return;
    }

    // Image slide → advance after configured duration.
    var self = this;
    this.imageAdvanceTimeout = setTimeout(function () {
      self.goToSlide(self.currentIndex + 1);
    }, this.autoplayDuration);
  };

  ProductShortsSlider.prototype.stopAutoAdvance = function () {
    if (this.imageAdvanceTimeout) {
      clearTimeout(this.imageAdvanceTimeout);
      this.imageAdvanceTimeout = null;
    }
  };

  ProductShortsSlider.prototype.pauseAllVideos = function () {
    this.slides.forEach(function (slide) {
      var video = slide.querySelector('video');
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });
  };

  ProductShortsSlider.prototype.togglePlay = function (slide) {
    var video = slide.querySelector('video');
    var playButton = slide.querySelector('.product-shorts-slide__media-play');
    if (!video) return;
    if (video.paused) {
      video.muted = this.muted;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () { /* play denied */ });
      }
    } else {
      video.pause();
    }
  };

  ProductShortsSlider.prototype.setPlayingState = function (playButton, playing) {
    if (!playButton) return;
    playButton.dataset.playing = playing ? 'true' : 'false';
    playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    if (playing) {
      playButton.dataset.idle = 'true';
    } else {
      playButton.dataset.idle = 'false';
    }
  };

  ProductShortsSlider.prototype.toggleMute = function () {
    this.muted = !this.muted;
    this.section.querySelectorAll('video').forEach(function (video) {
      video.muted = this.muted;
    }, this);
    this.syncMuteUI();
  };

  ProductShortsSlider.prototype.syncMuteUI = function () {
    var muted = this.muted;
    this.section.querySelectorAll('.product-shorts-slide__media-mute').forEach(function (btn) {
      btn.dataset.muted = muted ? 'true' : 'false';
      btn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      btn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    });
  };

  ProductShortsSlider.prototype.debounce = function (fn, delay) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, delay);
    };
  };

  ProductShortsSlider.prototype.destroy = function () {
    if (this.displayMode === 'grid') {
      this.closeModal();
      this.pauseGridVideos();
    } else {
      this.stopAutoAdvance();
      this.pauseAllVideos();
    }
    this.cleanupFns.forEach(function (fn) {
      try { fn(); } catch (e) { /* swallow */ }
    });
    this.cleanupFns = [];
  };

  function init(root) {
    var sections = (root || document).querySelectorAll(SECTION_SELECTOR);
    sections.forEach(function (section) {
      if (instances.has(section)) return;
      instances.set(section, new ProductShortsSlider(section));
    });
  }

  function destroy(section) {
    var instance = instances.get(section);
    if (instance) {
      instance.destroy();
      instances.delete(section);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroy(section);
  });
})();
